<script setup lang="ts">
import { ProjectMapTemplate } from '@beeboard/common';
import { orderBy } from 'lodash-es';
import { useRoute } from 'vue-router';
import { useProfileStore } from '@/store/profileStore';
import { useProjectStore } from '@/store/projectStore';
import { useTemplateStore } from '@/store/templateStore';
import SelectPhases, { SubmitData } from './SelectPhases.vue';
import SelectTemplate from './SelectTemplate.vue';

type Stage = 'selectTemplate' | 'selectPhases';

const emit = defineEmits<{ (e: 'close'): void }>();

const route = useRoute();
const templateStore = useTemplateStore();
const projectStore = useProjectStore();
const profileStore = useProfileStore();
const message = useAlerts();

const loading = ref(false);
const currentStage = ref<Stage>('selectTemplate');
const selectedTemplate = ref<ProjectMapTemplate | null>(null);

const templates = computed(() => {
  return templateStore.consumableTemplateLinks
    .filter((template) =>
      profileStore.profile.scopes.template_reader.some((scope) =>
        template.scopes.includes(scope),
      ),
    )
    .sort((a, b) => a.title.localeCompare(b.title));
});

const projectId = computed(() => {
  return route.params.projectId as string;
});

const project = computed(() => {
  return projectStore.project(projectId.value);
});

const projectPhases = computed(() => {
  return orderBy(
    project.value.phases || [],
    'final_milestone.current_deadline',
  );
});

const projectActivities = computed(() => {
  return project.value.activities || [];
});

const templatePhases = computed(() => {
  const phases = orderBy(selectedTemplate.value?.phases || [], 'position');

  return Array.from(phases);
});

onMounted(templateStore.loadList);

function prev() {
  switch (currentStage.value) {
    case 'selectPhases':
      currentStage.value = 'selectTemplate';
      break;

    default:
      throw Error("Can't handle prev for this stage");
  }
}

async function setSelectedTemplate(templateId: string) {
  if (!templateId) {
    return;
  }

  if (!templateStore.hasTemplate(templateId)) {
    loading.value = true;
    await templateStore.loadTemplate(templateId);
  }

  loading.value = false;

  selectedTemplate.value = templateStore.template(templateId);
  currentStage.value = 'selectPhases';
}

function onSubmit(data: SubmitData) {
  if (!selectedTemplate.value) {
    return;
  }

  const payload = {
    template_id: selectedTemplate.value.id,
    project_phase_id: data.projectPhaseId,
    template_phase_id: data.templatePhaseId,
    deadlines: data.deadlines,
  };

  loading.value = true;

  void projectStore
    .updateProjectFromTemplate(projectId.value, payload)
    .then(() => {
      message.success();
      emit('close');
    })
    .catch(message.error)
    .finally(() => {
      loading.value = false;
    });
}
</script>
<template>
  <UiModal
    is-wide
    :title="$t('projectUpdateFromTemplateModal.title')"
    @close="emit('close')"
  >
    <SelectTemplate
      v-if="currentStage === 'selectTemplate'"
      :loading="loading"
      :templates="templates"
      :origin-template-id="project.template"
      @next="setSelectedTemplate"
      @cancel="emit('close')"
    />

    <SelectPhases
      v-if="currentStage === 'selectPhases'"
      :loading="loading"
      :start-date="project.start_date"
      :project-id="projectId"
      :project-phases="projectPhases"
      :project-activities="projectActivities"
      :template-phases="templatePhases"
      @back="prev"
      @submit="onSubmit"
    />
  </UiModal>
</template>
